import React, { useState, useEffect } from 'react';
import { 
  MagnifyingGlassIcon, 
  TagIcon, 
  GlobeAltIcon, 
  EnvelopeIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  BriefcaseIcon,
  FunnelIcon,
  XMarkIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  Squares2X2Icon,
  AcademicCapIcon
} from '@heroicons/react/24/outline';
import { getDiscounts } from '../lib/db';
import { useNavigate } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import FavoriteButton from '../components/common/FavoriteButton';


const LazyLoadImageWithFade = ({ src, alt, placeholderSrc, className = '', ...props }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const handleImageLoad = () => setIsImageLoaded(true);

  return (
    <div className="relative w-full h-48 overflow-hidden rounded-t-lg bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
      <LazyLoadImage
        src={src}
        alt={alt}
        effect="blur"
        placeholderSrc={placeholderSrc || src}
        afterLoad={handleImageLoad}
        className={`absolute inset-0 w-full h-full object-cover ${className}`}
        wrapperClassName="absolute inset-0 w-full h-full"
        {...props}
      />
    </div>
  );
};

// We'll extract tags from the fetched discounts
const Marketplace = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [copiedCode, setCopiedCode] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [categories, setCategories] = useState(["All"]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    locations: [],
    headcount: [],
    tags: []
  });
  const [availableFilters, setAvailableFilters] = useState({
    locations: [],
    headcount: [],
    tags: []
  });
  const navigate = useNavigate();

  // Custom order for company size
  const COMPANY_SIZE_ORDER = [
    '0-10',
    '11-50',
    '51-100',
    '101-250',
    '0-250',
    '251-500',
    '250-1000',
    '500-1000',
    '1000+',
    '1001+',
  ];

  function sortCompanySizes(sizes) {
    const order = COMPANY_SIZE_ORDER;
    return sizes.slice().sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }

  // Initial fetch to get all discounts and available filters
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      // console.log("Discounts: Starting to fetch initial data");
      try {
        const discountsData = await getDiscounts();
        // console.log("Discounts: Data received", discountsData.length);
        setDiscounts(discountsData);

        // Categories: flatten, dedupe, trim, ignore null
        const allCategories = discountsData.flatMap(d => Array.isArray(d.category) ? d.category : []);
        const uniqueCategories = Array.from(new Set(allCategories.map(c => c && c.trim()).filter(Boolean)));
        setCategories(["All", ...uniqueCategories]);

        // Locations: robust parse, dedupe, add All
        const allLocations = discountsData.flatMap(d => Array.isArray(d.locations) ? d.locations : []);
        let uniqueLocations = Array.from(new Set(allLocations.filter(Boolean)));
        uniqueLocations = ["All", ...uniqueLocations.filter(l => l !== "All")];

        // Headcount: flatten, dedupe, trim, sort, add All
        const allHeadcounts = discountsData.flatMap(d => Array.isArray(d.headcount) ? d.headcount : []);
        let uniqueHeadcounts = Array.from(new Set(allHeadcounts.map(h => h && h.trim()).filter(Boolean)));
        uniqueHeadcounts = sortCompanySizes(uniqueHeadcounts);
        uniqueHeadcounts = ["All", ...uniqueHeadcounts.filter(h => h !== "All")];

        // Tags: flatten, dedupe, trim, add All
        const allTags = discountsData.flatMap(d => Array.isArray(d.tags) ? d.tags : []);
        let uniqueTags = Array.from(new Set(allTags.map(t => t && t.trim()).filter(Boolean)));
        uniqueTags = ["All", ...uniqueTags.filter(t => t !== "All")];

        setAvailableFilters({
          locations: uniqueLocations,
          headcount: uniqueHeadcounts,
          tags: uniqueTags
        });
      } catch (error) {
        console.error("Error fetching discounts:", error);
      } finally {
        // console.log("Discounts: Setting loading to false");
        setLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  // Fetch filtered discounts when search term, category, or filters change
  useEffect(() => {
    const fetchFilteredDiscounts = async () => {
      setLoading(true);
      try {
        // console.log(`Fetching filtered discounts:`, { searchTerm, category: selectedCategory, filters });
        const filteredData = await getDiscounts(searchTerm, selectedCategory, filters);
        setDiscounts(filteredData);
      } catch (error) {
        console.error("Error fetching filtered discounts:", error);
      } finally {
        setLoading(false);
      }
    };

    // Small delay to avoid too many requests while typing
    const timeoutId = setTimeout(() => {
      fetchFilteredDiscounts();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory, filters]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleFilter = (type, value) => {
    setFilters(prev => ({
      ...prev,
      [type]: prev[type].includes(value)
        ? prev[type].filter(v => v !== value)
        : [...prev[type], value]
    }));
  };

  const clearFilters = () => {
    setFilters({
      locations: [],
      headcount: [],
      tags: []
    });
    setSelectedCategory("All");
    setSearchTerm("");
  };

  const FilterButton = ({ type, value, icon: Icon }) => (
    <button
      onClick={() => {
        if (value === 'All') {
          setFilters(prev => ({ ...prev, [type]: [] }));
        } else {
          toggleFilter(type, value);
        }
      }}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
        (value === 'All' && filters[type].length === 0) || (value !== 'All' && filters[type].includes(value))
          ? 'bg-primary text-white'
          : 'bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white'
      }`}
    >
      {Icon && <Icon className="h-4 w-4" />}
      {value}
    </button>
  );

  // Helper to build filter summary
  const getFilterSummary = () => {
    const parts = [];
    if (selectedCategory !== "All") parts.push(selectedCategory);
    if (filters.locations.length > 0) parts.push(filters.locations.join(', '));
    if (filters.headcount.length > 0) parts.push(filters.headcount.join(', '));
    if (filters.tags.length > 0) parts.push(filters.tags.join(', '));
    if (searchTerm) parts.push(`"${searchTerm}"`);
    return parts.join(', ');
  };

  return (
    <div className="w-full mx-auto max-w-7xl">
      {/* Enhanced Header Section */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl border border-gray-700 p-6 mb-8 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-6 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Marketplace</h1>
            <p className="text-gray-300 text-base">Discover exclusive perks, offers, and deals for members</p>
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
            <input
              type="text"
              placeholder="Search marketplace..."
              className="w-full py-4 pl-12 pr-4 bg-gray-900 border-2 border-gray-600 rounded-xl text-white text-base focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/50 transition-all"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-4 rounded-xl text-base font-semibold transition-all flex items-center gap-3 ${
              showFilters 
                ? 'bg-primary text-white shadow-lg ring-2 ring-primary/50' 
                : 'bg-gray-700 text-gray-200 hover:bg-gray-600 hover:text-white border-2 border-gray-600'
            }`}
          >
            <FunnelIcon className="h-6 w-6" />
            <span className="hidden sm:inline">Filters</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-800 rounded-xl p-4 mb-6 space-y-4 sticky top-0 z-20 shadow-lg border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-white">Filters</h2>
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white transition-colors underline underline-offset-2"
            >
              Clear all
            </button>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-sm font-medium text-gray-400 mb-2">Categories</h3>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category}
                  onClick={() => handleCategorySelect(category)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                  }`}
                >
                  {category === 'HR Tech' ? (
                    <span className="flex items-center gap-1.5">
                      <ComputerDesktopIcon className="h-4 w-4" />
                      {category}
                    </span>
                  ) : category === 'HR Services' ? (
                    <span className="flex items-center gap-1.5">
                      <BriefcaseIcon className="h-4 w-4" />
                      {category}
                    </span>
                  ) : category === 'Personal Development' ? (
                    <span className="flex items-center gap-1.5">
                      <AcademicCapIcon className="h-4 w-4" />
                      {category}
                    </span>
                  ) : (
                    category
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Locations */}
          {availableFilters.locations.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Locations</h3>
              <div className="flex flex-wrap gap-2">
                {availableFilters.locations.map(location => (
                  <FilterButton
                    key={location}
                    type="locations"
                    value={location}
                    icon={MapPinIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Company Size */}
          {availableFilters.headcount.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Company Size</h3>
              <div className="flex flex-wrap gap-2">
                {availableFilters.headcount.map(size => (
                  <FilterButton
                    key={size}
                    type="headcount"
                    value={size}
                    icon={BuildingOfficeIcon}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          {availableFilters.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {availableFilters.tags.map(tag => (
                  <FilterButton
                    key={tag}
                    type="tags"
                    value={tag}
                    icon={TagIcon}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active filters */}
      {(filters.locations.length > 0 || filters.headcount.length > 0 || filters.tags.length > 0 || selectedCategory !== "All") && (
        <div className="flex flex-wrap gap-2 mb-6">
          {selectedCategory !== "All" && (
            <span className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              {selectedCategory}
              <button
                onClick={() => setSelectedCategory("All")}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          )}
          {filters.locations.map(location => (
            <span key={location} className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              <MapPinIcon className="h-4 w-4" />
              {location}
              <button
                onClick={() => toggleFilter('locations', location)}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
          {filters.headcount.map(size => (
            <span key={size} className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              <BuildingOfficeIcon className="h-4 w-4" />
              {size}
              <button
                onClick={() => toggleFilter('headcount', size)}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
          {filters.tags.map(tag => (
            <span key={tag} className="flex items-center gap-1.5 px-3 py-1 bg-primary/20 text-primary rounded-full text-sm">
              <TagIcon className="h-4 w-4" />
              {tag}
              <button
                onClick={() => toggleFilter('tags', tag)}
                className="hover:text-primary-dark"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Result count and filter summary */}
      <div className="mb-6">
        <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-gray-900/50 rounded-lg border border-gray-800">
          <Squares2X2Icon className="h-4 w-4 text-gray-600" />
          <span className="text-gray-400">
            {discounts.length} {discounts.length === 1 ? 'offer' : 'offers'}
          </span>
          {getFilterSummary() && (
            <span className="text-gray-500 text-xs">
              • {getFilterSummary()}
            </span>
          )}
        </div>
      </div>

      {/* Loading state */}
      {loading ? (
        <div className="text-center py-12 bg-gray-900 rounded-lg">
          <div className="animate-pulse flex flex-col items-center">
            <div className="h-8 w-36 bg-gray-800 rounded mb-4"></div>
            <div className="h-4 w-64 bg-gray-800 rounded"></div>
          </div>
        </div>
      ) : discounts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {discounts.map(discount => (
            <div 
              key={discount.id} 
              className="bg-gray-900 rounded-lg overflow-hidden shadow-lg flex flex-col h-full cursor-pointer hover:shadow-xl transition-shadow relative"
              onClick={() => navigate(`/marketplace/${discount.slug || discount.id}`)}
            >
              <div className="absolute top-3 right-3 z-10">
                <FavoriteButton
                  itemId={discount.id}
                  itemType="discount"
                  size="md"
                />
              </div>
              <div className="relative">
                <LazyLoadImageWithFade
                  src={discount.image_url}
                  alt={discount.name}
                  placeholderSrc={discount.image_url}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-4 sm:p-5 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    {discount.discount_icon_url && (
                      <img 
                        src={discount.discount_icon_url} 
                        alt={`${discount.name} logo`} 
                        className="h-8 w-8 object-contain rounded-lg bg-white p-1 border border-gray-700 flex-shrink-0" 
                      />
                    )}
                    <span className="text-sm font-semibold text-white truncate">{discount.name}</span>
                  </div>
                  {discount.category && Array.isArray(discount.category) && discount.category.length > 0 ? (
                    <span className="text-xs font-medium bg-gray-800 text-gray-300 px-2.5 py-1 rounded-full border border-gray-700 flex items-center gap-1.5 flex-shrink-0">
                      {discount.category[0] === 'HR Tech' ? (
                        <ComputerDesktopIcon className="h-3.5 w-3.5" />
                      ) : discount.category[0] === 'HR Services' ? (
                        <BriefcaseIcon className="h-3.5 w-3.5" />
                      ) : discount.category[0] === 'Personal Development' ? (
                        <AcademicCapIcon className="h-3.5 w-3.5" />
                      ) : (
                        <UserGroupIcon className="h-3.5 w-3.5" />
                      )}
                      {discount.category[0]}
                    </span>
                  ) : (
                    <span className="text-xs font-medium bg-gray-800/50 text-gray-400 px-2.5 py-1 rounded-full border border-gray-700 flex items-center gap-1.5 flex-shrink-0">
                      <UserGroupIcon className="h-3.5 w-3.5" />
                      Uncategorized
                    </span>
                  )}
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-white mb-2 line-clamp-2">
                  {discount.offer || 'Membership Offer'}
                </h3>
                <p className="text-sm text-gray-400 mb-4 line-clamp-3">{discount.description}</p>
                
                {/* Tags section */}
                {discount.tags && discount.tags.length > 0 && (
                  <div className="mt-auto mb-3 flex flex-wrap gap-1.5">
                    {discount.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="text-xs font-medium bg-gray-800/50 text-gray-400 px-2 py-0.5 rounded-full border border-gray-700"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Contact and website links */}
                <div className="flex flex-wrap gap-2">
                  {discount.website_url && (
                    <a 
                      href={discount.website_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center text-xs text-primary hover:text-primary-light transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <GlobeAltIcon className="h-3.5 w-3.5 mr-1" />
                      Visit Website
                    </a>
                  )}
                  {discount.contact_email && (
                    <a 
                      href={`mailto:${discount.contact_email}`}
                      className="flex items-center text-xs text-primary hover:text-primary-light transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EnvelopeIcon className="h-3.5 w-3.5 mr-1" />
                      Contact
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 sm:py-12 bg-gray-900 rounded-lg">
          <div className="text-4xl sm:text-5xl mb-4">🏷️</div>
          <h3 className="text-base sm:text-lg font-medium text-white mb-2">No marketplace offers found</h3>
          <p className="text-sm text-gray-500">Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default Marketplace; 